import { HospitalNames } from '@shared/models/hospitalTypes.model';
import moment from 'moment';
import { profileCard } from '../components/day-program-recap/day-program-interfaces';
import { Effective } from '../components/effective-needs/interfaces';
import { OVERFLOW_THRESHOLD, SURGEON, TOPICS } from '../const/globals.const';
import { DoctorAgenda } from '../models/doctor-agenda.model';
import { Formation } from '../models/formation.model';
import { Hospital, HospitalWithDisplayName } from '../models/hospital.model';
import { Profile } from '../models/profile.model';
import { Room } from '../models/room.model';
import { Specialty } from '../models/specialty.model';
import { User } from '../models/user.model';

/**
 * Converts a Date object to ISO string format (YYYY-MM-DD) using local time, NOT UTC.
 *
 * This function directly extracts the year, month, and day components from the Date object
 * in the browser's local timezone, avoiding the UTC conversion that toISOString() performs.
 *
 * Use this function when you need to send or store dates to an API and want to preserve
 * the local date without timezone offset conversion. For example, if the user selects
 * "February 1st" in their timezone, this will return "2026-02-01" (not "2026-01-31"
 * which would be the UTC result for timezone GMT+1).
 *
 * @param date The Date object to format
 * @returns ISO date string in format YYYY-MM-DD based on local time
 */
export function formatDateAsISO(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// <- In practice, these are hospitals
export function addDisplayNameToHospitals(hospitals: HospitalWithDisplayName[]) {
  const allHospitalNames = hospitals.map((h) => h.hospitalName);
  const hasMultipleHospitalNames = new Set(allHospitalNames).size > 1;

  if (!hasMultipleHospitalNames) {
    hospitals.forEach((hospital: any) => (hospital.displayName = hospital.name));
  } else {
    hospitals.forEach((hospital: any) => (hospital.displayName = hospital.displayedHospitalName));
  }
}

export function formatCompareTime(time: Date) {
  const tmp = [time.getUTCHours(), time.getUTCMinutes(), time.getUTCSeconds()];
  return tmp[0] * 3600 + tmp[1] * 60 + tmp[2];
}

export function sortByDate(array: any[]): any[] {
  return array.sort((a: any, b: any): any => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
}

export function setTypeAttributeArray(roomsData: any[], isConfigMode: boolean): string[] {
  const attrArray: string[] = [];
  for (const roomData of roomsData) {
    if (isConfigMode) {
      attrArray.push('surgeonopeninghistory');
    } else {
      if (roomData.surgeonopening && roomData.surgeonopening.length > 0) {
        attrArray.push('surgeonopening');
      } else {
        attrArray.push('surgeonopeninghistory');
      }
    }
  }
  return attrArray;
}

export function idStringGenerator() {
  const S4 = function () {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  return S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4();
}

export function RemoveAccents(str: any): string {
  const accents = 'ÀÁÂÃÄÅàáâãäåÒÓÔÕÕÖØòóôõöøÈÉÊËèéêëðÇçÐÌÍÎÏìíîïÙÚÛÜùúûüÑñŠšŸÿýŽž';
  const accentsOut = 'AAAAAAaaaaaaOOOOOOOooooooEEEEeeeeeCcDIIIIiiiiUUUUuuuuNnSsYyyZz';
  str = str.split('');
  const strLen = str.length;
  let i;
  let x;
  for (i = 0; i < strLen; i++) {
    if ((x = accents.indexOf(str[i])) !== -1) {
      str[i] = accentsOut[x];
    }
  }
  return str.join('');
}

export function transformText(text: string, hospitalNames: HospitalNames, isNurse?: boolean): string {
  const {
    CALENDAR,
    VACATIONS,
    SMART_BRIEF,
    PARCOURS_IBODE_REQUEST,
    PARCOURS_IBODE_RECAP,
    PROGRAM,
    MESSAGERIE,
    STATOP,
  } = TOPICS;

  switch (text) {
    case MESSAGERIE:
      return 'Messagerie';
    case CALENDAR:
      return 'Calendriers';
    case VACATIONS:
      return 'Calendriers';
    case SMART_BRIEF:
      return 'Smart brief';
    case PARCOURS_IBODE_REQUEST:
      return 'Compétences';
    case PARCOURS_IBODE_RECAP:
      return 'Compétences';
    case PROGRAM:
      return 'SmartPlanning';
    case STATOP:
      return 'StatOp';
    case 'AVAILABLE2':
      return 'Disponible';
    case 'ECHANGE_DE_JOUR':
      return 'Echange de jour';
    case 'ABSENT':
      return 'Absence';
    case 'BLOC':
      return hospitalNames.blocName;
    case 'AVAILABLE':
      return 'Présence';
    case 'PARTIAL_AVAILABILITY':
      return 'Présence partielle';
    case 'CONSULTATION':
      return hospitalNames.consultationName;
    case 'CONGRESS':
      return 'Congrès';
    case 'TRAINING':
      return 'Formation';
    case 'SICKNESS':
      return 'Maladie';
    case 'VACATION':
      return 'Vacation';
    case 'GUARD':
      return isNurse ? 'Astreinte' : 'Garde';
    case 'CP':
      return 'Congé payé';
    case 'GUARD_REST':
      return isNurse ? "Repos d'astreinte" : 'Repos de garde';
    case 'OTHER':
      return 'Autre';

    case 'ONHOLD':
      return 'En attente';
    case 'APPROVED':
      return 'Approuvée';
    case 'REJECTED':
      return 'Rejetée';
    case 'OPEN':
      return 'Ouverte';

    default:
      return text;
  }
}

export function transformedDate(stringDate: string): Date {
  return moment(stringDate).clone().utc().add({ hour: 12 }).set({ hour: 12 }).toDate();
}

export function capitalize(s: string) {
  if (!s || !s.length) {
    return '';
  }
  const text = s.toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function geFloatNumber(number: number, numberAfter: number): number {
  if (number === null || number === undefined) {
    return number;
  }
  let num = 0;
  if (number.toString().includes('.')) {
    num = parseFloat(number.toFixed(numberAfter));
  } else {
    return (num = number);
  }
  return num;
}

export function escapeRegExp(string: string): string {
  if (string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }
  return string;
}

export function toPascalCase(str: string): string {
  if (!str) {
    return str;
  }
  return str.replace(/\p{L}+/gu, (word: string) => {
    const firstChar = word.charAt(0).toUpperCase();
    const rest = word.slice(1).toLowerCase();
    return firstChar + rest;
  });
}

export function toFixed(number: number, numberOfNumbersAfterDecimialPoint: number): string {
  const multiplier = Math.pow(10, numberOfNumbersAfterDecimialPoint);
  const result = Math.round(number * multiplier) / multiplier;
  return `${result}`;
}

export function toFixedDown(number: number, numberOfNumbersAfterDecimialPoint: number): string {
  const multiplier = Math.pow(10, numberOfNumbersAfterDecimialPoint);
  const result = Math.floor(number * multiplier) / multiplier;
  return `${result}`;
}

export function dateWithTime(date: any, time: any): Date | null {
  date = new Date(date);
  time = new Date(time);
  const hour = time.getUTCHours(),
    minutes = time.getUTCMinutes(),
    secondes = time.getUTCSeconds();
  date = new Date(date).setUTCHours(hour);
  date = new Date(date).setUTCMinutes(minutes);
  date = new Date(date).setUTCSeconds(secondes);
  if (date) {
    return date;
  } else {
    return null;
  }
}

export function getRandomColor(index: number) {
  const colors = [
    '#8EA9DB',
    '#B4C6E7',
    '#D9E1F2',
    '#03C0C1',
    '#808080',
    '#BFBFBF',
    '#00D8D8',
    '#007F80',
    '#03FDFC',
    '#009C9D',
    '#A6A6A6',
    '#F2F2F2',
    '#D9D9D9',
  ];

  return colors[index];
}

export function initIsDifferentSurgeonsBetweenSurgeonOpeningsAndDoctorAgendas(
  roomsAllData: any[],
  roomsSurgeonOpeningsAllData: any[]
): void {
  // init isDifferentSurgeonsBetweenSurgeonOpeningsAndDoctorAgendas for all rooms
  roomsAllData.forEach((elt: any) => {
    const surgeonOpening = roomsSurgeonOpeningsAllData.find(
      (elt2: any) => String(elt2.room._id) === String(elt.room._id)
    );

    const displayAlert = displayDifferenceAlert(surgeonOpening, elt);
    const doesDoctorAgendasHaveConflicts = checkIfDoctorAgendasHaveConflicts(elt.doctoragendas);

    surgeonOpening.isDifferentSurgeonsBetweenSurgeonOpeningsAndDoctorAgendas = displayAlert; //delete?
    elt.isDifferentSurgeonsBetweenSurgeonOpeningsAndDoctorAgendas = displayAlert;

    surgeonOpening.doesDoctorAgendasHaveConflicts = doesDoctorAgendasHaveConflicts; //delete?
    elt.doesDoctorAgendasHaveConflicts = doesDoctorAgendasHaveConflicts;
  });
}

function checkIfDoctorAgendasHaveConflicts(doctorAgendas: DoctorAgenda[]): boolean {
  doctorAgendas = doctorAgendas.sort(
    (a: DoctorAgenda, b: DoctorAgenda) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  for (let i = 0; i < doctorAgendas.length - 1; i++) {
    if (doctorAgendas[i].endTime > doctorAgendas[i + 1].startTime) {
      return true;
    }
  }

  return false;
}

function displayDifferenceAlert(sOdata: any, dAdata: any) {
  const selectedHospital = dAdata.room.hospital._id;
  const surgeonOpeningsRecords = sOdata.surgeonopening.filter((SO: any) => SO.opening);
  const doctorAgendasRecords = dAdata.doctoragendas;

  // We don't have any doctor agendas but have so => we show the alert
  // We don't have any doctor agendas and don't have any so => we don't show the alert
  if (!doctorAgendasRecords.length && surgeonOpeningsRecords.length) {
    // return true;
    return false;
  } else if (!doctorAgendasRecords.length) {
    return false;
  }

  // We have doctor agendas and we don't have any surgeon opening => we show the alert
  if (!surgeonOpeningsRecords.length) {
    return true;
  }

  // We have doctor agendas and we have surgoen openings, we need to check for each doctor agenda that we have a correspond surgeon opening
  for (const daRecord of doctorAgendasRecords) {
    const doctorAgendaSpecialty =
      daRecord &&
      daRecord.surgeon.specialties &&
      daRecord.surgeon.specialties.filter((specialty: any) => specialty.hospital === selectedHospital)[0];

    let foundACorrespondingSurgeonOpening = false;

    for (const soRecord of surgeonOpeningsRecords) {
      // Check if it is a surgeon opening with the same surgeon
      if (daRecord.surgeon._id === soRecord.surgeon._id) {
        foundACorrespondingSurgeonOpening = true;
        break;
      }

      // Check if it is a surgeon opening with Urgences specialty
      const surgeonOpeningSpecialty =
        soRecord.surgeon.specialties &&
        soRecord.surgeon.specialties.filter((specialty: any) => specialty.hospital === selectedHospital)[0];

      // If we have Urgences, we don't need to check for any doctor agenda, we don't show the alert
      if (surgeonOpeningSpecialty.isEmergency) {
        return false;
      }

      // Check if it is a surgeon opening with a generic surgeon that have the same specialty
      if (soRecord.surgeon.isDefaultSpecialtySurgeon && doctorAgendaSpecialty._id === surgeonOpeningSpecialty._id) {
        foundACorrespondingSurgeonOpening = true;
        break;
      }
    }

    if (!foundACorrespondingSurgeonOpening) {
      return true;
    }
  }

  // If we didn't quit before means every doctor agenda have a correspond surgeon opening
  return false;
}

export const byStartTime = (a: any, b: any): number =>
  new Date(a.startTime).getTime() - new Date(b.startTime).getTime();

export function mergeRoomsWithBufferPrograms(bufferRooms: any[], openingRooms: any[], isPastDate: boolean): any[] {
  const bufferByRoomId = new Map<string, any[]>();
  for (const room of bufferRooms) {
    if (room._id) {
      bufferByRoomId.set(String(room._id), room.programs || []);
    }
  }

  const result: any[] = [];
  const openingCoveredRoomIds = new Set<string>();

  for (const openingRoom of openingRooms) {
    const roomData = openingRoom.room ?? openingRoom;
    const roomId = roomData._id ? String(roomData._id) : null;
    if (!roomId || openingCoveredRoomIds.has(roomId)) {
      continue;
    }

    const raw: any[] = isPastDate
      ? openingRoom.surgeonopeninghistory?.length
        ? openingRoom.surgeonopeninghistory
        : openingRoom.surgeonopening || []
      : openingRoom.surgeonopening || [];
    const entries = raw.filter((e: any) => e.opening === true);
    if (!entries.length) {
      continue;
    }

    openingCoveredRoomIds.add(roomId);
    const matchingBufferPrograms = bufferByRoomId.get(roomId) || [];

    const matchedBpIndices = new Set<number>();
    const programs = entries.map((entry: any) => {
      const entrySurgeonId = entry.surgeon?._id || entry.surgeon;
      const matchIdx = matchingBufferPrograms.findIndex((bp: any) => {
        const bpSurgeonId = bp.surgeon?._id || bp.surgeon;
        return bpSurgeonId === entrySurgeonId && bp.startTime === entry.startTime;
      });
      if (matchIdx !== -1) {
        matchedBpIndices.add(matchIdx);
        return matchingBufferPrograms[matchIdx];
      }
      return {
        surgeon: entry.surgeon,
        startTime: entry.startTime,
        endTime: entry.endTime,
        anesthesists: [],
        nurses: [],
        type: entry.type,
      };
    });

    // Include buffer programs not matched to any opening entry (e.g. afternoon part of a half-day split)
    matchingBufferPrograms.forEach((bp: any, idx: number) => {
      if (!matchedBpIndices.has(idx)) {
        programs.push(bp);
      }
    });

    result.push({ ...roomData, programs: programs.sort(byStartTime) });
  }

  for (const room of bufferRooms) {
    if (room._id && !openingCoveredRoomIds.has(String(room._id)) && room.programs?.length) {
      result.push({ ...room, programs: [...(room.programs || [])].sort(byStartTime) });
    }
  }

  return result;
}

export function sortRoomsByPriority(rooms: any, isItArrayOfRooms: boolean) {
  if (!rooms) {
    return;
  }

  rooms.sort((a: any, b: any) => {
    const itemA = isItArrayOfRooms ? a : a.room;
    const itemB = isItArrayOfRooms ? b : b.room;
    if ((!itemA.priority || itemA.priority === 0) && (!itemB.priority || itemB.priority === 0)) {
      return itemA._id.localeCompare(itemB._id);
      // return 0;
    } else if (!itemA.priority || itemA.priority === 0) {
      return 1;
    } else if (!itemB.priority || itemB.priority === 0) {
      return -1;
    } else {
      const itemAHospitalId = itemA.hospital._id ? itemA.hospital._id : itemA.hospital;
      const itemBHospitalId = itemB.hospital._id ? itemB.hospital._id : itemB.hospital;

      const hospitalOrder = itemAHospitalId.localeCompare(itemBHospitalId);
      if (hospitalOrder === 0) {
        const priorityOrder = itemA.priority - itemB.priority;
        if (priorityOrder === 0) {
          return itemA._id.localeCompare(itemB._id);
        } else {
          return priorityOrder;
        }
      } else {
        return hospitalOrder;
      }
    }
  });
}

export function getSurgeonSpecialtiesNameBySelectedHospitals(profile: any, selectedHospitalsId: string[]) {
  const specialtiesName: any[] = [];
  if (profile && profile.position === SURGEON && profile.specialties && profile.specialties.length > 0) {
    selectedHospitalsId.forEach((hospitalId: string) => {
      if (Array.isArray(profile.specialties)) {
        profile.specialties.forEach((specialty: any) => {
          if (specialty) {
            if (String(specialty.hospital) === String(hospitalId)) {
              specialtiesName.push(specialty.name);
            }
          }
        });
      } else {
        specialtiesName.push(profile.specialties.name);
      }
    });
  }
  return specialtiesName;
}

export function getSurgeonSpecialtiesBySelectedHospitals(profile: Profile, selectedHospitalsId: string[]) {
  const specialties: any[] = [];
  if (profile.position === SURGEON) {
    selectedHospitalsId.forEach((hospitalId: string) => {
      profile.specialties?.forEach((specialty: Specialty) => {
        if (String(specialty.hospital) === String(hospitalId)) {
          specialties.push(specialty);
        }
      });
    });
  }
  return specialties;
}

export function getHospitalsBySelectedHospitals(profile: Profile, selectedHospitalsId: string[]) {
  const hospitals: any[] = [];
  selectedHospitalsId.forEach((hospitalId: string) => {
    profile.hospitals?.forEach((hospital: Hospital) => {
      if (String(hospital._id) === String(hospitalId)) {
        hospitals.push(hospital);
      }
    });
  });
  return hospitals;
}

export function getHospitalsNameBySelectedHospitals(
  profile: Profile,
  profileHospitalsIds: string[],
  usersHospitals: HospitalWithDisplayName[]
) {
  const hospitalsName: any[] = [];
  // selectedHospitalsId.forEach((hospitalId: string) => {
  // if (profile.desactivatedAt && profile.desactivatedAt.some((objectId: string) => String(objectId) === hospitalId)) {
  //   return;
  // }

  //   profileHospitalsIds.forEach((hospitalId: string) => {
  //     hospitalsName.push(
  //       userHospitals.find((hospital: HospitalWithDisplayName) => String(hospital._id) === hospitalId)?.displayName
  //     );
  //   });

  //   // profile.hospitals?.forEach((hospital: Hospital) => {
  //   //   if (typeof hospital === 'string') {
  //   //     if (String(hospital) === String(hospitalId)) {
  //   //       hospitalsName.push(
  //   //         userHospitals.find((hospital2: Hospital) => String(hospital2._id) === String(hospital))?.name
  //   //       );
  //   //     }
  //   //   } else {
  //   //     if (String(hospital._id) === String(hospitalId)) {
  //   //       hospitalsName.push(hospital.name);
  //   //     }
  //   //   }
  //   // });
  // });
  profileHospitalsIds.forEach((hospId: string) => {
    if (profile.desactivatedAt && profile.desactivatedAt.some((objectId: string) => String(objectId) === hospId)) {
      return;
    }

    const hospital = usersHospitals.find((hosp) => String(hosp._id) === hospId);
    hospitalsName.push(hospital?.displayName);
  });
  return hospitalsName;
}

export function setOverflowDatasColors(input: any): void {
  if (input.overflowDuration != null) {
    if (Math.abs(input.overflowDuration) > OVERFLOW_THRESHOLD) {
      if (input.overflowDuration < 0) {
        input.overflowText = 'faible';
        input.overflowColor = 'green';
      } else {
        input.overflowText = 'élevée';
        input.overflowColor = 'red';
      }
    } else {
      input.overflowText = 'modérée';
      input.overflowColor = 'orange';
    }
  }
}

export function getRateColor(overflowRate: number): string {
  const ORANGE_RATE_THRESHOLD = 5;

  if (overflowRate > ORANGE_RATE_THRESHOLD) {
    return 'red';
  } else if (overflowRate > 0) {
    return 'orange';
  } else {
    return 'green';
  }
}

export function calculateOverflowRate(openedDuration: number, overflowDuration: number, precision: number): number {
  if (openedDuration > 0) {
    if (overflowDuration > 0) {
      return Number(((overflowDuration / openedDuration) * 100).toFixed(precision));
    } else {
      return 0;
    }
  } else {
    if (overflowDuration > 0) {
      return 100;
    } else {
      return 0;
    }
  }
}

export function getFirstHospitalSelectedData(user: User, selectedHospitals: any[]): Hospital | undefined {
  if (user && user.profile && user.profile.hospitals && selectedHospitals) {
    const userHospitals = user.profile.hospitals;
    const hospital = userHospitals.find((hospital: Hospital) => String(hospital._id) === String(selectedHospitals[0]));
    return hospital;
  }

  return undefined;
}

export function getSelectedHospitalData(user: any, hospitalId: string) {
  const userHospitals = user.profile.hospitals;
  const hospital = userHospitals.find((hospital: any) => String(hospital._id) === String(hospitalId));
  return hospital;
}

export function getSelectedHospitalsData(user: any, hospitalIds: string[]) {
  const userHospitals = user.profile.hospitals;
  const selectedHospitals = userHospitals.filter((hospital: any) => hospitalIds.includes(String(hospital._id)));
  return selectedHospitals;
}

export function getWorkForce(formations: Formation[], nurses: profileCard[], middleTime: string): Effective {
  const workforce: Effective = { morningNurseEffectif: [], afternoonNurseEffectif: [] };

  formations?.forEach((formation: Formation) => addFormationToGroups(formation, workforce));
  nurses?.forEach((nurse: profileCard) => addNurseToGroups(nurse, workforce, middleTime));

  return workforce;
}

function addFormationToGroups(formation: Formation, workforce: Effective) {
  if (formation.groupedWith?.length != 0 && isAlreadyAdded(formation, workforce)) {
    return;
  }

  const formationsTab = formation.groupedWith?.length === 0 ? [formation] : [formation, formation.groupedWith![0]];

  workforce.morningNurseEffectif.push({ formations: formationsTab, morningNurses: 0 });
  workforce.afternoonNurseEffectif.push({ formations: formationsTab, afternoonNurses: 0 });
}

function addNurseToGroups(nurse: profileCard, workforce: Effective, middleTime: string) {
  const midTime = getHoursAndMinutes(middleTime);

  for (let index = 0; index < workforce.morningNurseEffectif.length; index++) {
    if (
      workforce.morningNurseEffectif[index].formations.find(
        (formation: any) => formation.name === nurse?.profile?.position
      )
    ) {
      const startTime = getHoursAndMinutes(nurse.time?.startTime ?? nurse.originalTime?.startTime ?? '');
      if (!nurse.originalTime?.startTime && !nurse.originalTime?.endTime) {
        return;
      }
      if (startTime <= midTime && startTime >= getHoursAndMinutes(nurse.originalTime?.startTime ?? '')) {
        workforce.morningNurseEffectif[index].morningNurses++;
      }
      const endTime = getHoursAndMinutes(nurse.time?.endTime ?? nurse.originalTime?.endTime ?? '');
      if (endTime > midTime && endTime <= getHoursAndMinutes(nurse?.originalTime?.endTime ?? '')) {
        workforce.afternoonNurseEffectif[index].afternoonNurses++;
      }
      return;
    }
  }
}

function isAlreadyAdded(newFormation: Formation, workforce: Effective): boolean {
  return (
    workforce.morningNurseEffectif.find(
      (workforce: any) =>
        workforce.formations[0].groupedWith.length != 0 && workforce.formations[1].name == newFormation.name
    ) != undefined
  );
}

export function getHoursAndMinutes(date: string | number | Date | undefined): string {
  if (!date) {
    return '';
  }
  const d = new Date(date);
  d.setTime(d.getTime() + d.getTimezoneOffset() * 60 * 1000);

  return `${d.getHours() <= 9 ? '0' + d.getHours() : d.getHours()}:${d.getMinutes() <= 9 ? '0' + d.getMinutes() : d.getMinutes()}`;
}

/**
 *
 * @param elem The string to calc the max size
 * @param style The style of elem (ex: Inter italic 500 11.5)
 * @param elementId The id of the element which will contain the string
 * @param errorMargin The error margin for the element width to add to the calc width
 * @param replacement The replacement string in case of width higher than offsetWidth
 * @returns Return the replacement if one, or the maxWidthElem or the elem
 */
export function getMaxSizedString(
  elem: string,
  style: string,
  elementId: string,
  errorMargin: number = 0,
  replacement?: string
) {
  const pageElem = document.getElementById(elementId);

  if (pageElem) {
    const elemWidth = getTextWidth(elem, style);
    return elemWidth + errorMargin > pageElem.offsetWidth
      ? replacement
        ? replacement
        : getMaxWidthElem(pageElem.offsetWidth, elem, style, errorMargin)
      : elem;
  } else {
    return elem;
  }
}

/**
 * Return the maximum sized text based on classname
 * @param elem the text to transform
 * @param style text style
 * @param className class of the parent of the elem
 * @param errorMargin the offset we can allow for the width
 * @returns the transformed text if it overflows the max width allowed
 */
export function getMaxSizedText(
  elem: string,
  style: string,
  className: string,
  errorMargin: number = 0,
  replacement?: string
) {
  const parentElem = document.getElementsByClassName(className)[0];
  if (parentElem) {
    const elemWidth = getTextWidth(elem, style);
    return elemWidth + errorMargin > (parentElem as HTMLElement).offsetWidth
      ? replacement
        ? replacement
        : getMaxWidthElem((parentElem as HTMLElement).offsetWidth, elem, style, errorMargin)
      : elem;
  } else {
    return elem;
  }
}

function getTextWidth(text: string, font: string) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return 0;
  }
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

function getMaxWidthElem(offsetWidth: number, elem: string, style: string, errorMargin: number) {
  let copyElem = (' ' + elem).slice(1);
  let elemWidth = getTextWidth(copyElem, style);
  const maxIteration = 30;
  let nbIterations = 0;

  while (elemWidth + errorMargin > offsetWidth && nbIterations < maxIteration) {
    copyElem = copyElem.substring(0, copyElem.length - 2);
    copyElem = copyElem + '.';
    elemWidth = getTextWidth(copyElem, style);
    nbIterations++;
  }

  if (copyElem.length < 3) {
    return elem.substring(0, 3) + '.';
  }

  return copyElem;
}

export function getRoomHoursBySelectedDay(room: Room, date: Date) {
  const time = { startTime: new Date(), endTime: new Date() };

  const tmp = room.horaire?.find((h: any) => h.day === date.getDay() + 1);
  if (tmp) {
    time.startTime = new Date(tmp.start);
    time.endTime = new Date(tmp.end);
  }

  return time;
}

export function getBrightness(color: string) {
  // Remove the hash if it's there
  color = color.replace('#', '');

  // Convert hex to RGB
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  // Calculate brightness
  // This uses the luminance formula: (0.299 * R) + (0.587 * G) + (0.114 * B)
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  const dayOfWeek = moment(date).format('dddd');
  const dayOfMonth = moment(date).format('DD');
  const month = moment(date).format('MMMM');
  const time = moment(date).format('HH:mm'); // Add time formatting

  return `${dayOfWeek.slice(0, 1).toUpperCase()}${dayOfWeek.slice(1)} ${dayOfMonth} ${month} ${time}`;
}

export function matchesUrlLikeServiceWorker(url: string, pattern: string): boolean {
  // Escape regex special characters in the pattern, then replace
  // the wildcard '**' and '*' with appropriate regex patterns
  const regexPattern = pattern
    .replace(/([.+?^${}()|[\]\\])/g, '\\$1') // Escape special characters
    .replace(/(\*\*\/)/g, '.*') // '**/' matches any path
    .replace(/(\*\*)/g, '.*') // '**' matches any characters
    .replace(/(\*)/g, '[^/]*') // '*' matches any sequence of characters except '/'
    .replace(/\/$/g, '\/?'); // Allow an optional trailing slash

  const regex = new RegExp(`${regexPattern}`); // Start and end anchors

  return regex.test(url);
}

export function getPreviousDates(fromDate: any, toDate: any): [number, Date, Date] {
  const fromDateUtc = moment.utc(fromDate);
  const toDateUtc = moment.utc(toDate);
  const daysDifference = toDateUtc.diff(fromDateUtc, 'days');

  let previousStart: moment.Moment;
  let previousEnd: moment.Moment;
  let weekNumber: number;

  if (daysDifference > 7) {
    previousStart = fromDateUtc.subtract(1, 'year');
    previousEnd = toDateUtc.subtract(1, 'year');
    weekNumber = 0;
  } else {
    ({ previousStart, previousEnd, weekNumber } = getPreviousWeekDates(fromDateUtc));
  }

  return [weekNumber, previousStart.toDate(), previousEnd.toDate()];
}

function getPreviousWeekDates(fromDateUtc: moment.Moment) {
  const previousYear = fromDateUtc.year() - 1;
  const weekNumber = fromDateUtc.isoWeek();
  const previousStart = moment().year(previousYear).isoWeek(weekNumber).startOf('isoWeek');
  const previousEnd = moment().year(previousYear).isoWeek(weekNumber).endOf('isoWeek');

  return { previousStart, previousEnd, weekNumber };
}

export function formatDuration(startTime: Date | string, endTime: Date | string): string {
  const diffSeconds = moment(endTime).diff(moment(startTime), 'seconds');

  const minutes = Math.floor(diffSeconds / 60);
  const seconds = diffSeconds % 60;

  if (minutes === 0) {
    return `${seconds}s`;
  }
  if (seconds === 0) {
    return `${minutes}min`;
  }
  return `${minutes}min${seconds}s`;
}
